// Player card + the slim DTO shape Discover and SavedPlayers both render.
//
// Shared between /discover (paginated search results) and /saved (the
// caller's bookmarked players). Both endpoints emit `PlayerSearchResult`
// — keep the shape changes flowing through this file so the two pages
// can't drift.

import { Link } from 'react-router-dom'
import { ArrowLeftRight } from 'lucide-react'
import type { AvailabilityStatus, PlayerPosition } from '../types'
import Avatar from './Avatar'
import { useCompare } from '../context/CompareContext'
import { useState } from 'react'

export interface PlayerSearchResult {
  id: string
  slug: string
  name: string
  photo_url: string | null
  position: PlayerPosition | null
  nationality: string | null
  height_cm: number | null
  age: number | null
  is_verified: boolean
  created_at: string
  updated_at: string
  // Phase 2 wide — present on /api/players/search and /api/saved-players
  // results. PlayerCard ignores these for now; a follow-up session will
  // render a "no agent attached" badge when source === 'api_sports'.
  source: 'agent' | 'api_sports'
  agent_id: string | null
  // UIR-1 — headline stats from the player's most recent season + current team
  availability_status: AvailabilityStatus
  current_team: string | null
  headline_stats: {
    season: string
    pts_avg: number
    reb_avg: number
    ast_avg: number
  } | null
}

// Recency tag is derived client-side so the API response stays slim and
// the labels stay fresh between renders. "New to market" wins over
// "Recently updated" when both apply.
function recencyTag(r: PlayerSearchResult): { label: string; tone: 'green' | 'blue' } | null {
  const now = Date.now()
  const created = Date.parse(r.created_at)
  if (Number.isFinite(created) && now - created < 14 * 86400_000) {
    return { label: 'New to market', tone: 'green' }
  }
  const updated = Date.parse(r.updated_at)
  if (Number.isFinite(updated) && now - updated < 7 * 86400_000) {
    return { label: 'Recently updated', tone: 'blue' }
  }
  return null
}

// UIR-1 — availability status label mapping (extracted from Discover.tsx for reuse)
const AVAILABILITY_LABEL: Record<AvailabilityStatus, string> = {
  open: 'Open to offers',
  evaluating: 'Evaluating offers',
  closed: 'Not available',
}

export default function PlayerCard({ player }: { player: PlayerSearchResult }) {
  const tag = recencyTag(player)
  const compare = useCompare()
  const [showFeedback, setShowFeedback] = useState(false)

  // UIR-1 — compact header meta: Position · Country · Team (omit absent segments)
  const metaParts = [
    player.position,
    player.nationality,
    player.current_team,
  ].filter(Boolean)

  const isInCompare = compare.has(player.slug)

  const handleCompareClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const result = compare.add(player.slug)
    if (!result.success) {
      // Already in set or full — show feedback
      setShowFeedback(true)
      setTimeout(() => setShowFeedback(false), 2000)
    } else if (!isInCompare) {
      // Success feedback for add
      setShowFeedback(true)
      setTimeout(() => setShowFeedback(false), 2000)
    } else {
      // Remove
      compare.remove(player.slug)
    }
  }

  return (
    <Link
      to={`/players/${player.slug}`}
      className="group relative block bg-hoop-surface border border-hoop-border rounded-[10px] p-4 hover:border-hoop-accent transition-colors"
    >
      {/* Compare button — top-right corner */}
      <button
        onClick={handleCompareClick}
        className={`absolute top-2 right-2 p-1.5 rounded-md transition-colors ${
          isInCompare
            ? 'bg-hoop-accent text-white'
            : 'bg-hoop-bg text-hoop-text-3 hover:bg-hoop-accent-light hover:text-hoop-accent'
        }`}
        aria-label={isInCompare ? 'Remove from compare' : 'Add to compare'}
        title={isInCompare ? 'Remove from compare' : 'Add to compare'}
      >
        <ArrowLeftRight className="w-3.5 h-3.5" />
      </button>
      {/* Feedback toast */}
      {showFeedback && (
        <div className="absolute top-2 right-12 bg-hoop-text text-hoop-bg text-xs px-2 py-1 rounded shadow-lg z-10">
          {isInCompare ? 'Removed from compare' : 'Added to compare'}
        </div>
      )}
      {/* Header row: 44px avatar · name · meta line */}
      <div className="flex items-center gap-[11px] mb-3.5">
        {/* 44px rounded avatar tile with onError fallback to initials */}
        <Avatar
          src={player.photo_url}
          name={player.name}
          className="w-11 h-11 rounded-[9px] flex-shrink-0"
          textClassName="text-base"
        />
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold text-[15.5px] leading-tight text-hoop-text truncate">
            {player.name}
          </h3>
          <div className="text-[12.5px] text-hoop-text-3 truncate">
            {metaParts.length > 0 ? metaParts.join(' · ') : '—'}
          </div>
        </div>
      </div>

      {/* Stat strip: 3-cell bordered row (PTS / REB / AST) OR dashed empty state */}
      {player.headline_stats ? (
        <div className="flex mb-3 border border-hoop-border rounded-lg overflow-hidden">
          <div className="flex-1 py-2 px-2 text-center border-r border-hoop-border">
            <div className="font-stat font-bold text-[15px] leading-tight text-hoop-text">
              {player.headline_stats.pts_avg.toFixed(1)}
            </div>
            <div className="text-[9.5px] uppercase tracking-wider text-hoop-text-3 mt-0.5">
              PTS
            </div>
          </div>
          <div className="flex-1 py-2 px-2 text-center border-r border-hoop-border">
            <div className="font-stat font-bold text-[15px] leading-tight text-hoop-text">
              {player.headline_stats.reb_avg.toFixed(1)}
            </div>
            <div className="text-[9.5px] uppercase tracking-wider text-hoop-text-3 mt-0.5">
              REB
            </div>
          </div>
          <div className="flex-1 py-2 px-2 text-center">
            <div className="font-stat font-bold text-[15px] leading-tight text-hoop-text">
              {player.headline_stats.ast_avg.toFixed(1)}
            </div>
            <div className="text-[9.5px] uppercase tracking-wider text-hoop-text-3 mt-0.5">
              AST
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-3 border border-dashed border-hoop-border rounded-lg py-3 px-3 text-center text-xs text-hoop-text-3">
          No games logged yet
        </div>
      )}

      {/* Badge row: verified + availability + recency + unrepresented */}
      <div className="flex flex-wrap gap-1.5">
        {player.is_verified && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-surface border border-[#B9CADD] text-hoop-text">
            ✓ Verified
          </span>
        )}
        {/* Availability chip — amber for evaluating, other statuses styled similarly */}
        {player.availability_status === 'evaluating' && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-amber-bg text-hoop-amber">
            {AVAILABILITY_LABEL[player.availability_status]}
          </span>
        )}
        {player.availability_status === 'open' && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-green-bg text-hoop-green">
            {AVAILABILITY_LABEL[player.availability_status]}
          </span>
        )}
        {player.availability_status === 'closed' && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-red-bg text-hoop-red">
            {AVAILABILITY_LABEL[player.availability_status]}
          </span>
        )}
        {tag && (
          <span
            className={
              'inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] ' +
              (tag.tone === 'green'
                ? 'bg-hoop-green-bg text-hoop-green'
                : 'bg-hoop-accent-light text-hoop-accent')
            }
          >
            {tag.label}
          </span>
        )}
        {player.agent_id === null && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-bg text-hoop-text-2">
            Unrepresented
          </span>
        )}
        {/* M6.4 — represented chip (when agent_id present) mirrors mock "Represented" chips */}
        {player.agent_id !== null && player.availability_status !== 'evaluating' && (
          <span className="inline-flex items-center px-2 py-[3px] rounded-[5px] text-[10.5px] font-semibold tracking-[0.03em] bg-hoop-accent-light text-hoop-accent">
            Represented
          </span>
        )}
      </div>
    </Link>
  )
}
